import { console_api } from '../http/consoleClient';
import {
  ConsoleManagedTable,
  ConsoleTableCatalogItem,
  ConsoleTableProfileResponse,
  RecomputeProfileMode,
  RecomputeProfileResponse,
} from 'types/console/tables.types';

export const consoleTablesApi = {
  getTableCatalog: async (requesterEmail: string): Promise<ConsoleTableCatalogItem[]> => {
    const response = await console_api.get<{ tables: ConsoleTableCatalogItem[] }>('/console/tables/catalog', {
      params: { requester_email: requesterEmail },
    });
    return response.data.tables;
  },

  getDailyRefresh: async (tableId: number, requesterEmail: string): Promise<boolean> => {
    const response = await console_api.get<{ id: number; daily_refresh: boolean }>(
      `/console/tables/catalog/${tableId}/daily-refresh`,
      { params: { requester_email: requesterEmail } }
    );
    return response.data.daily_refresh;
  },

  upsertTableConfiguration: async (payload: {
    requester_email: string;
    table_id: number;
    snowflakes_table_name: string;
    postgres_table_name: string;
    daily_refresh: boolean;
    primary_developer_email: string;
    secondary_developer_email: string;
    table_description: string;
    aliases: Array<{ column_name: string; alias_name: string }>;
    permission_emails: string[];
  }): Promise<ConsoleManagedTable> => {
    const response = await console_api.post<ConsoleManagedTable>('/console/tables/config', payload);
    return response.data;
  },

  assignAdminTable: async (payload: {
    requester_email: string;
    table_id: number;
    primary_developer_email: string;
    secondary_developer_email: string;
    daily_refresh: boolean;
  }): Promise<ConsoleManagedTable> => {
    const response = await console_api.post<ConsoleManagedTable>('/console/admin/tables/assign', payload);
    return response.data;
  },

  updateTableColumnFields: async (payload: {
    requester_email: string;
    table_id: number;
    fields: Array<{ field_key: string; field_label?: string }>;
  }): Promise<ConsoleManagedTable> => {
    const response = await console_api.patch<ConsoleManagedTable>(
      `/console/admin/tables/${payload.table_id}/column-fields`,
      {
        requester_email: payload.requester_email,
        fields: payload.fields,
      }
    );
    return response.data;
  },

  getConfiguredTables: async (requesterEmail: string, includeDeleted = false): Promise<ConsoleManagedTable[]> => {
    const response = await console_api.get<{ tables: ConsoleManagedTable[] }>('/console/tables', {
      params: { requester_email: requesterEmail, include_deleted: includeDeleted },
    });
    return response.data.tables;
  },

  // Used by ConsoleDashboard to populate source table selectors
  listAvailableTables: async (requesterEmail: string): Promise<ConsoleManagedTable[]> => {
    const response = await console_api.get<{ tables: any[] }>('/console/dashboards/list_available_tables', {
      params: { requester_email: requesterEmail },
    });
    // Normalize: this endpoint returns `id` instead of `table_id`
    return (response.data.tables || []).map((t) => ({
      ...t,
      table_id: t.table_id ?? t.id,
    }));
  },

  setTableSoftDelete: async (payload: {
    requester_email: string;
    table_id: number;
    is_deleted: boolean;
  }): Promise<ConsoleManagedTable> => {
    const response = await console_api.patch<ConsoleManagedTable>(
      `/console/tables/${payload.table_id}/soft-delete`,
      {
        requester_email: payload.requester_email,
        is_deleted: payload.is_deleted,
      }
    );
    return response.data;
  },

  approveTable: async (payload: {
    requester_email: string;
    table_id: number;
    stage: 'developer' | 'admin';
    is_approved: boolean;
    note?: string;
  }): Promise<ConsoleManagedTable> => {
    const response = await console_api.patch<ConsoleManagedTable>(
      `/console/tables/${payload.table_id}/approval`,
      {
        requester_email: payload.requester_email,
        stage: payload.stage,
        is_approved: payload.is_approved,
        note: payload.note || '',
      }
    );
    return response.data;
  },

  updateTableSettings: async (payload: {
    requester_email: string;
    table_id: number;
    table_description: string;
    aliases: Array<{ column_name: string; alias_name: string }>;
    permission_emails: string[];
  }): Promise<ConsoleManagedTable> => {
    const response = await console_api.patch<ConsoleManagedTable>(`/console/tables/${payload.table_id}/settings`, {
      requester_email: payload.requester_email,
      table_description: payload.table_description,
      aliases: payload.aliases,
      permission_emails: payload.permission_emails,
    });
    return response.data;
  },

  setColumnActive: async (payload: {
    requester_email: string;
    table_id: number;
    column_name: string;
    is_active: boolean;
  }): Promise<void> => {
    await console_api.patch(`/console/tables/${payload.table_id}/columns/${encodeURIComponent(payload.column_name)}/active`, {
      requester_email: payload.requester_email,
      is_active: payload.is_active,
    });
  },

  setColumnFieldValues: async (payload: {
    requester_email: string;
    table_id: number;
    column_name: string;
    field_values: Record<string, string>;
  }): Promise<void> => {
    await console_api.patch(
      `/console/tables/${payload.table_id}/columns/${encodeURIComponent(payload.column_name)}/fields`,
      {
        requester_email: payload.requester_email,
        field_values: payload.field_values,
      }
    );
  },

  recomputeProfile: async (
    tableId: number,
    requesterEmail: string,
    options?: {
      recompute_mode?: RecomputeProfileMode;
      confirm_delete_text?: string;
    }
  ): Promise<RecomputeProfileResponse> => {
    const response = await console_api.post<RecomputeProfileResponse>(`/console/tables/${tableId}/recompute-profile`, {
      requester_email: requesterEmail,
      confirm_recompute: true,
      recompute_mode: options?.recompute_mode || 'incremental_sync',
      confirm_delete_text: options?.confirm_delete_text || undefined,
    });
    return response.data;
  },

  getTableProfile: async (tableId: number, requesterEmail: string): Promise<ConsoleTableProfileResponse> => {
    const response = await console_api.get<ConsoleTableProfileResponse>(`/console/tables/${tableId}/profile`, {
      params: { requester_email: requesterEmail },
    });
    return response.data;
  },

  revertProfile: async (payload: {
    requester_email: string;
    table_id: number;
    target_version: number;
  }): Promise<{
    table_id: number;
    profile_version: number;
    reverted_from_version: number;
    updated_at: string;
  }> => {
    const response = await console_api.post<{
      table_id: number;
      profile_version: number;
      reverted_from_version: number;
      updated_at: string;
    }>(`/console/tables/${payload.table_id}/revert-profile`, {
      requester_email: payload.requester_email,
      target_version: payload.target_version,
    });
    return response.data;
  },
};
